import {
  WorkspaceDocument,
  WorkspaceFolder,
  getAllWorkspaceDocuments,
} from "@/models/workspace/Workspace";
import Workspace from "@/models/workspace/Workspace";
import { logger } from "@/lib/logger";
import { UploadSource } from "@/components/upload/types/upload-item";
import {
  IdentifiedChange,
  CloudFileMetadata,
  CloudFileCheckStatus,
  MissingCloudDocumentInfo,
  createEmptyIdentifiedChange,
} from "@/types/sync.types";
import {
  fetchFolderMetadata,
  fetchFolderContentsDirect,
  checkCloudFileStatus,
  determineUploadSource,
  GraphCallOptions,
} from "@/services/microsoft/graphApiService";
import {
  GraphRequestError,
  describeGraphFailure,
} from "@/services/microsoft/graphFetch";
import {
  getAllSharePointFolders,
  getAllDocumentsFromFolder,
  findFolderBySharePointId,
} from "../utils/workspaceItemUtils";

/**
 * Result of recursively fetching folder contents
 */
type RecursiveFolderContentsResult = {
  files: Array<{
    fileName: string;
    /** lastModifiedDateTime from the listing */
    lastModified?: string;
    folderPath: string;
    folderMetadata: CloudFileMetadata;
    fileMetadata: CloudFileMetadata;
    parentFolderId: string;
  }>;
  folders: Array<{
    folder: WorkspaceFolder | null;
    folderPath: string;
    metadata: CloudFileMetadata;
    parentFolderId?: string;
  }>;
};

/**
 * Lists a workspace folder's cloud counterpart. A 404 means the linked folder
 * itself is gone, so it has no children; each of its documents is then
 * checked one by one. Any other failure propagates.
 */
const listLinkedFolder = async (
  folder: WorkspaceFolder,
  graphOptions: GraphCallOptions
) => {
  try {
    return await fetchFolderContentsDirect(folder, graphOptions);
  } catch (error) {
    if (error instanceof GraphRequestError && error.kind === "notFound") {
      return { files: [], folders: [] };
    }
    throw error;
  }
};

const folderReadError = (folderName: string, error: unknown): string =>
  `Mappen "${folderName}" kunne ikke læses (${describeGraphFailure(
    error
  )}). Prøv igen om lidt.`;

/**
 * Checks each document that is absent from the folder listing. A 404 or 403
 * is reported as missing/inaccessible; a check that couldn't be completed
 * (throttling, an outage) is not, since it says nothing about the file.
 */
const verifyAbsentDocuments = async (
  documents: WorkspaceDocument[],
  folderName: string,
  graphOptions: GraphCallOptions
): Promise<{ missing: MissingCloudDocumentInfo[]; error?: string }> => {
  const missing: MissingCloudDocumentInfo[] = [];
  let unchecked = 0;
  for (const doc of documents) {
    const status = await checkCloudFileStatus(
      doc.driveId,
      doc.siteId,
      doc.sharepointFileId!,
      graphOptions
    );
    if (status === CloudFileCheckStatus.UNKNOWN) {
      unchecked++;
    } else if (status !== CloudFileCheckStatus.EXISTS) {
      missing.push({ document: doc, folderPath: folderName, status });
    }
  }
  const error =
    unchecked > 0
      ? `${unchecked} fil${unchecked === 1 ? "" : "er"} i mappen "${folderName}" kunne ikke kontrolleres på SharePoint/OneDrive. Prøv igen om lidt.`
      : undefined;
  return { missing, error };
};

const recordListedFileModified = (
  result: IdentifiedChange,
  files: RecursiveFolderContentsResult["files"]
) => {
  for (const file of files) {
    const id = file.fileMetadata.sharepointFileId;
    const modified = new Date(file.lastModified ?? Number.NaN).getTime();
    if (id && Number.isFinite(modified)) {
      result.listedFileModified.set(id, modified);
    }
  }
};

/**
 * Hook that provides change identification functionality for folder sync
 */
export const useChangeIdentification = (workspace: Workspace | null) => {
  /**
   * Recursively collects all documents from a folder tree
   */
  const getAllDocumentsFromFolderTree = (
    folder: WorkspaceFolder
  ): WorkspaceDocument[] => {
    const documents: WorkspaceDocument[] = [];
    documents.push(...folder.documents);

    folder.folders.forEach((subfolder) => {
      documents.push(...getAllDocumentsFromFolderTree(subfolder));
    });

    return documents;
  };

  /**
   * Recursively fetches folder contents building folder tree structure
   */
  const fetchFolderContentsRecursively = async (
    folder: WorkspaceFolder,
    parentPath: string | null,
    graphOptions: GraphCallOptions,
    onProgress?: (message: string) => void
  ): Promise<RecursiveFolderContentsResult> => {
    const result: RecursiveFolderContentsResult = {
      files: [],
      folders: [],
    };

    if (!folder.sharepointFileId || !folder.driveId) {
      return result;
    }

    // Path rules:
    // - Root folder: exclude its name from paths (relative paths inside the workspace folder)
    // - Subfolders: include their names as expected (e.g., "A", "A/B", ...)
    const currentPath =
      parentPath === null
        ? ""
        : parentPath
          ? `${parentPath}/${folder.name}`
          : folder.name;
    const folderSource = determineUploadSource(folder.siteId);

    if (onProgress) {
      const displayPath = currentPath || folder.name;
      onProgress(`Henter indhold fra mappe: ${displayPath}...`);
    }

    const folderContents = await listLinkedFolder(folder, graphOptions);

    // Process files in current folder
    for (const fileInfo of folderContents.files) {
      result.files.push({
        fileName: fileInfo.name,
        lastModified: fileInfo.lastModifiedDateTime,
        folderPath: currentPath,
        folderMetadata: {
          source: folderSource,
          driveId: folder.driveId,
          siteId: folder.siteId,
          sharepointFileId: folder.sharepointFileId,
        },
        fileMetadata: {
          source: fileInfo.source,
          driveId: fileInfo.driveId,
          siteId: fileInfo.siteId,
          sharepointFileId: fileInfo.sharepointFileId || "",
        },
        parentFolderId: folder.id,
      });
    }

    // Process subfolders recursively
    for (const subfolderInfo of folderContents.folders) {
      const existingSubfolder = findFolderBySharePointId(
        folder.folders,
        subfolderInfo.sharepointFileId
      );

      const subfolderPath = currentPath
        ? `${currentPath}/${subfolderInfo.name}`
        : subfolderInfo.name;

      result.folders.push({
        folder: existingSubfolder || null,
        folderPath: subfolderPath,
        metadata: {
          source: determineUploadSource(subfolderInfo.siteId),
          driveId: subfolderInfo.driveId,
          siteId: subfolderInfo.siteId,
          sharepointFileId: subfolderInfo.sharepointFileId,
        },
        parentFolderId: folder.id,
      });

      if (existingSubfolder) {
        // Subfolder exists - recursively fetch its contents
        const subfolderResult = await fetchFolderContentsRecursively(
          existingSubfolder,
          currentPath,
          graphOptions,
          onProgress
        );
        result.files.push(...subfolderResult.files);
        result.folders.push(...subfolderResult.folders);
      } else {
        // Subfolder doesn't exist in workspace yet - fetch from SharePoint directly (fully recursive)
        await fetchRemoteFolderContentsRecursively(
          folder,
          subfolderInfo,
          subfolderPath,
          result,
          graphOptions
        );
      }
    }

    return result;
  };

  /**
   * Fetches contents of a folder that doesn't exist in the workspace yet (fully recursive)
   */
  const fetchRemoteFolderContentsRecursively = async (
    rootFolder: WorkspaceFolder,
    folderInfo: {
      id: string;
      name: string;
      driveId?: string;
      siteId?: string;
      sharepointFileId: string;
    },
    folderPath: string,
    result: RecursiveFolderContentsResult,
    graphOptions: GraphCallOptions
  ): Promise<void> => {
    const folderContents = await fetchFolderContentsDirect(
      {
        ...rootFolder,
        sharepointFileId: folderInfo.sharepointFileId,
        name: folderInfo.name,
        driveId: folderInfo.driveId || rootFolder.driveId,
        siteId: folderInfo.siteId || rootFolder.siteId,
      } as WorkspaceFolder,
      graphOptions
    );

    const folderSource = determineUploadSource(folderInfo.siteId);

    for (const fileInfo of folderContents.files) {
      result.files.push({
        fileName: fileInfo.name,
        lastModified: fileInfo.lastModifiedDateTime,
        folderPath: folderPath,
        folderMetadata: {
          source: folderSource,
          driveId: folderInfo.driveId,
          siteId: folderInfo.siteId,
          sharepointFileId: folderInfo.sharepointFileId,
        },
        fileMetadata: {
          source: fileInfo.source,
          driveId: fileInfo.driveId,
          siteId: fileInfo.siteId,
          sharepointFileId: fileInfo.sharepointFileId || "",
        },
        // Files in non-existent folders are still created under the existing workspace root folder.
        parentFolderId: rootFolder.id,
      });
    }

    for (const subfolder of folderContents.folders) {
      const subfolderPath = `${folderPath}/${subfolder.name}`;

      result.folders.push({
        folder: null,
        folderPath: subfolderPath,
        metadata: {
          source: determineUploadSource(subfolder.siteId),
          driveId: subfolder.driveId,
          siteId: subfolder.siteId,
          sharepointFileId: subfolder.sharepointFileId,
        },
        parentFolderId: rootFolder.id,
      });

      await fetchRemoteFolderContentsRecursively(
        rootFolder,
        {
          id: subfolder.id,
          name: subfolder.name,
          driveId: subfolder.driveId,
          siteId: subfolder.siteId,
          sharepointFileId: subfolder.sharepointFileId,
        },
        subfolderPath,
        result,
        graphOptions
      );
    }
  };

  /**
   * Identifies changes for a single folder and its subfolders
   */
  const identifyFolderChanges = async (
    folder: WorkspaceFolder,
    onProgress?: (message: string) => void,
    graphOptions: GraphCallOptions = {}
  ): Promise<IdentifiedChange> => {
    const result = createEmptyIdentifiedChange();

    if (!workspace || !folder.sharepointFileId || !folder.driveId) {
      return result;
    }

    const folderTreeDocuments = getAllDocumentsFromFolderTree(folder);
    const existingSharePointIds = new Set(
      folderTreeDocuments
        .map((doc) => doc.sharepointFileId)
        .filter((id): id is string => !!id)
    );

    const processedFileIds = new Set<string>();
    const processedFolderKeys = new Set<string>();

    try {
      if (onProgress) {
        onProgress(`Identificerer ændringer i mappe: ${folder.name}...`);
      }

      // Check folder name
      const metadata = await fetchFolderMetadata(folder, graphOptions);
      if (metadata && metadata.name !== folder.name) {
        result.folderNameUpdates.push({
          folder,
          newName: metadata.name,
        });
      }

      // Fetch folder contents recursively
      const folderContents = await fetchFolderContentsRecursively(
        folder,
        null,
        graphOptions,
        onProgress
      );

      // Identify new folders (including empty)
      for (const folderInfo of folderContents.folders) {
        if (folderInfo.folder) continue;
        const key = `${folderInfo.parentFolderId || ""}::${folderInfo.folderPath}`;
        if (processedFolderKeys.has(key)) continue;
        processedFolderKeys.add(key);
        result.newFolders.push({
          folderPath: folderInfo.folderPath,
          metadata: folderInfo.metadata,
          parentFolderId: folderInfo.parentFolderId,
        });
      }

      recordListedFileModified(result, folderContents.files);

      // Identify new documents
      for (const fileInfo of folderContents.files) {
        const fileSharePointId = fileInfo.fileMetadata.sharepointFileId;
        if (
          fileSharePointId &&
          !existingSharePointIds.has(fileSharePointId) &&
          !processedFileIds.has(fileSharePointId)
        ) {
          processedFileIds.add(fileSharePointId);
          result.newDocuments.push({
            fileName: fileInfo.fileName,
            folderPath: fileInfo.folderPath,
            folderMetadata: fileInfo.folderMetadata,
            fileMetadata: fileInfo.fileMetadata,
            parentFolderId: fileInfo.parentFolderId,
          });
        }
      }

      // Identify documents in the Lara folder tree whose cloud file is not
      // visible in the linked SharePoint folder listing.
      const sharePointFileIds = new Set(
        folderContents.files
          .map((f) => f.fileMetadata.sharepointFileId)
          .filter((id): id is string => !!id)
      );

      const missingFromLinkedFolder = folderTreeDocuments.filter(
        (doc) =>
          doc.sharepointFileId &&
          doc.documentSource !== UploadSource.LOCAL &&
          !sharePointFileIds.has(doc.sharepointFileId)
      );

      // Verify each absent doc against Graph API to distinguish 404 (confirmed missing)
      // from permission errors (inaccessible). Files that are still accessible
      // elsewhere are simply not listed here — skip them.
      const verified = await verifyAbsentDocuments(
        missingFromLinkedFolder,
        folder.name,
        graphOptions
      );
      result.missingCloudDocuments.push(...verified.missing);
      if (verified.error) result.errors.push(verified.error);

      // All cloud docs in the folder tree should be synced regardless of deletion status.
      // The per-doc sync handles "no update needed" gracefully via the timestamp check.
      result.existingDocumentsToSync = folderTreeDocuments.filter(
        (doc) =>
          (doc.documentSource === "OneDrive" ||
            doc.documentSource === "SharePoint") &&
          (doc.sharepointFileId || doc.driveId)
      );
    } catch (error) {
      logger.error("Failed to identify sync changes for a folder", {
        operation: "identifyFolderChanges",
        error,
        status: error instanceof GraphRequestError ? error.status : undefined,
        workspaceId: workspace.id,
        folderId: folder.id,
      });
      result.errors.push(folderReadError(folder.name, error));
    }

    return result;
  };

  /**
   * Identifies all changes across all SharePoint folders in the workspace
   */
  const identifyAllChanges = async (
    onProgress?: (message: string) => void,
    graphOptions: GraphCallOptions = {}
  ): Promise<IdentifiedChange> => {
    const result = createEmptyIdentifiedChange();

    if (!workspace) {
      return result;
    }

    const foldersToSync = getAllSharePointFolders(workspace);

    const allExistingDocuments = getAllWorkspaceDocuments(workspace);
    const existingSharePointIds = new Set(
      allExistingDocuments
        .map((doc) => doc.sharepointFileId)
        .filter((id): id is string => !!id)
    );

    const processedFileIds = new Set<string>();
    const processedFolderIds = new Set<string>();
    const processedNewFolderKeys = new Set<string>();

    for (const folder of foldersToSync) {
      try {
        // Skip already processed folders
        if (
          folder.sharepointFileId &&
          processedFolderIds.has(folder.sharepointFileId)
        ) {
          continue;
        }
        if (folder.sharepointFileId) {
          processedFolderIds.add(folder.sharepointFileId);
        }

        if (onProgress) {
          onProgress(`Identificerer ændringer i mappe: ${folder.name}...`);
        }

        // Check folder name
        const metadata = await fetchFolderMetadata(folder, graphOptions);
        if (metadata && metadata.name !== folder.name) {
          result.folderNameUpdates.push({
            folder,
            newName: metadata.name,
          });
        }

        // Fetch folder contents recursively
        const folderContents = await fetchFolderContentsRecursively(
          folder,
          null,
          graphOptions,
          onProgress
        );

        // Identify new folders (including empty) for this root folder
        for (const folderInfo of folderContents.folders) {
          if (folderInfo.folder) continue;
          const key = `${folderInfo.parentFolderId || ""}::${folderInfo.folderPath}`;
          if (processedNewFolderKeys.has(key)) continue;
          processedNewFolderKeys.add(key);
          result.newFolders.push({
            folderPath: folderInfo.folderPath,
            metadata: folderInfo.metadata,
            parentFolderId: folderInfo.parentFolderId,
          });
        }

        recordListedFileModified(result, folderContents.files);

        // Identify new documents
        for (const fileInfo of folderContents.files) {
          const fileSharePointId = fileInfo.fileMetadata.sharepointFileId;
          if (
            fileSharePointId &&
            !existingSharePointIds.has(fileSharePointId) &&
            !processedFileIds.has(fileSharePointId)
          ) {
            processedFileIds.add(fileSharePointId);
            result.newDocuments.push({
              fileName: fileInfo.fileName,
              folderPath: fileInfo.folderPath,
              folderMetadata: fileInfo.folderMetadata,
              fileMetadata: fileInfo.fileMetadata,
              parentFolderId: fileInfo.parentFolderId,
            });
          }
        }

        // Identify documents in this folder whose cloud file is not visible in
        // the linked SharePoint folder listing.
        const existingDocumentsInFolder = getAllDocumentsFromFolder(folder);
        const sharePointFileIds = new Set(
          folderContents.files
            .map((f) => f.fileMetadata.sharepointFileId)
            .filter((id): id is string => !!id)
        );

        const missingFromLinkedFolder = existingDocumentsInFolder.filter(
          (doc) =>
            doc.sharepointFileId &&
            doc.documentSource !== UploadSource.LOCAL &&
            !sharePointFileIds.has(doc.sharepointFileId)
        );

        // Verify each absent doc against Graph API to distinguish 404 (confirmed missing)
        // from permission errors (inaccessible).
        const verified = await verifyAbsentDocuments(
          missingFromLinkedFolder,
          folder.name,
          graphOptions
        );
        result.missingCloudDocuments.push(...verified.missing);
        if (verified.error) result.errors.push(verified.error);
      } catch (error) {
        logger.error("Failed to identify sync changes for a folder", {
          operation: "identifyAllChanges",
          error,
          status: error instanceof GraphRequestError ? error.status : undefined,
          workspaceId: workspace.id,
          folderId: folder.id,
        });
        result.errors.push(folderReadError(folder.name, error));
      }
    }

    // All cloud docs in the workspace should be synced regardless of deletion status.
    const allDocuments = getAllWorkspaceDocuments(workspace);

    result.existingDocumentsToSync = allDocuments.filter(
      (doc) =>
        (doc.documentSource === "OneDrive" ||
          doc.documentSource === "SharePoint") &&
        (doc.sharepointFileId || doc.driveId)
    );

    // Dedupe missingCloudDocuments by document.id to avoid duplicates from overlapping linked folders
    const seenMissingIds = new Set<string>();
    result.missingCloudDocuments = result.missingCloudDocuments.filter((d) => {
      if (seenMissingIds.has(d.document.id)) return false;
      seenMissingIds.add(d.document.id);
      return true;
    });

    return result;
  };

  return {
    identifyFolderChanges,
    identifyAllChanges,
  };
};

export default useChangeIdentification;
